import { Switch } from '@ardoq/forms';
import { Header2 } from '@ardoq/typography';
import { ContextConfig } from 'viewpointBuilder/openViewpointBuilder/getContextConfig';
import { FilterByNameAndTypeCriteria } from './FilterByNameAndTypeCriteria';
import { ComponentsSearchCommands } from 'viewpointBuilder/selectContextTab/commands';
import { ComponentSearchViewState } from 'viewpointBuilder/selectContextTab/types';
import { AdvancedSearchQueryBuilder } from 'search/AdvancedSearch/AdvancedSearchQueryBuilder';
import { ViewpointModalHeader } from 'viewpointBuilder/components/ViewpointModalHeader';
import { Island } from '@ardoq/page-layout';
import { FlexBox } from '@ardoq/layout';
import { t, type I18nLocale } from '@ardoq/i18n';

type SelectContextTabHeaderProps = {
  state: ComponentSearchViewState;
  contextConfig: ContextConfig;
  commands: ComponentsSearchCommands;
  viewpointName: string;
  showHeaderRow?: boolean;
  /**
   * The modal header names the Viewpoint and describes opening it. Set to
   * `false` where the surrounding modal supplies its own header.
   */
  showModalHeader?: boolean;
  i18nLocale: I18nLocale;
  namespace: string;
};
const SelectContextTabSearch = ({
  state,
  contextConfig,
  commands,
  viewpointName,
  showHeaderRow = true,
  showModalHeader = true,
  i18nLocale,
  namespace,
}: SelectContextTabHeaderProps) => {
  return (
    <Island
      maxWidth={contextConfig.isNavigationPanelHidden ? '100%' : undefined}
    >
      {contextConfig.isNavigationPanelHidden && showModalHeader ? (
        <ViewpointModalHeader
          title={t(
            i18nLocale,
            'viewpointBuilder:selectContext.modal.title',
            'Select context component(s)'
          )}
          subtitle={t(
            i18nLocale,
            'viewpointBuilder:selectContext.modal.subtitle',
            { viewpointName },
            'Open the selected context component(s) with "{{viewpointName}}" Viewpoint'
          )}
        />
      ) : showHeaderRow ? (
        <FlexBox align="center" gap="medium" marginBottom="medium">
          <Header2>
            {t(
              i18nLocale,
              'viewpointBuilder:selectContext.search.title',
              'Component search'
            )}
          </Header2>

          <Switch
            isChecked={state.advancedSearchState !== null}
            onChange={() =>
              commands.setIsAdvancedSearch(!state.advancedSearchState)
            }
            name="advanced-search-switch"
            label={t(
              i18nLocale,
              'viewpointBuilder:selectContext.search.advancedSearch',
              'Advanced search'
            )}
          />
        </FlexBox>
      ) : null}
      {state.advancedSearchState !== null ? (
        <AdvancedSearchQueryBuilder
          namespace={namespace}
          contextSelectIsReadOnly
          hideContainerStyles
          onChange={({ isValid, isEmpty, query }) => {
            commands.viewpointBuilderAdvancedSearchQueryUpdated({
              isValid,
              isEmpty,
              query,
            });
          }}
        />
      ) : (
        <FilterByNameAndTypeCriteria
          {...contextConfig}
          componentTypeSelectionDisabledReason={t(
            i18nLocale,
            'viewpointBuilder:selectContext.componentTypeSelectionDisabledReason',
            'The context component type is set in the saved viewpoint.'
          )}
          componentTypes={state.componentTypes}
          setComponentName={commands.setComponentName}
          componentName={state.componentName}
          selectedComponentType={state.componentType}
          setSelectedComponentType={commands.setComponentTypeName}
          setIsAdvancedSearch={commands.setIsAdvancedSearch}
          i18nLocale={i18nLocale}
        />
      )}
    </Island>
  );
};

export { SelectContextTabSearch };
